import * as React from 'react'
import { createPortal } from 'react-dom'
import * as API from '../lib/api'

// Trial Explainer info box (CD-172): an anchored desktop popover / centered
// mobile modal showing the trial's per-product-type download breakdown.
// Hand-rolled because React 16 (CD-039) has no floating-ui/popper, and
// `react-modal` can't produce the anchored desktop look.

// Foundation's `small only` upper bound: strictly below this we render the
// centered mobile presentation, so 640px matches Foundation's breakpoint.
const MOBILE_MAX_WIDTH = 640
// Past this many items the desktop card balances into two columns; more items
// grow rows, never a third column.
const COLUMN_THRESHOLD = 8

// React 16 has no `useId`; hand out monotonically increasing per-instance ids
// for `aria-labelledby` (allocated once per mount via the lazy initializer below).
let explainerInstanceCount = 0

// Tabbable elements inside the dialog, used to bound the focus trap.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'

interface ITrialExplainerProps {
  planName: string
  // Already ordered by `SiteProductType#display_order` server-side.
  items: API.ITrialDownloadItem[]
  children: React.ReactNode
}

function isMobileViewport(): boolean {
  return typeof window !== 'undefined' && window.innerWidth < MOBILE_MAX_WIDTH
}

function downloadLabel(count: number): string {
  return `${count} ${count === 1 ? 'download' : 'downloads'}`
}

export function TrialExplainer({ planName, items, children }: ITrialExplainerProps) {
  const [open, setOpen] = React.useState(false)
  const [isMobile, setIsMobile] = React.useState(isMobileViewport)
  const [coords, setCoords] = React.useState<{ top: number; left: number } | null>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const boxRef = React.useRef<HTMLDivElement>(null)
  const closeButtonRef = React.useRef<HTMLButtonElement>(null)
  const [titleId] = React.useState(() => `trial-explainer-title-${(explainerInstanceCount += 1)}`)

  function close() {
    setOpen(false)
    if (triggerRef.current) { triggerRef.current.focus() }
  }

  // Track viewport size so the anchored-vs-centered presentation follows resize.
  React.useEffect(() => {
    function onResize() { setIsMobile(isMobileViewport()) }

    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // ESC and outside-click/tap dismissal, only wired while open.
  React.useEffect(() => {
    if (!open) { return undefined }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') { close() }
    }

    function onPointerDown(event: Event) {
      const target = event.target as Node
      if (boxRef.current && boxRef.current.contains(target)) { return }
      if (triggerRef.current && triggerRef.current.contains(target)) { return }
      setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('touchstart', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('touchstart', onPointerDown)
    }
  }, [open])

  // The Tab trap and body-scroll lock apply ONLY to the mobile modal; desktop is
  // a non-modal popover where, per the WAI-ARIA pattern, Tab should move out
  // naturally (ESC / outside-click still dismiss), so it is deliberately not
  // trapped. On open, focus moves into the dialog; `close()` restores the trigger.
  React.useEffect(() => {
    if (!open) { return undefined }
    const box = boxRef.current
    if (!box) { return undefined }

    const focusTarget = (isMobile && closeButtonRef.current) || box
    focusTarget.focus()

    if (!isMobile) { return undefined }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') { return }

      const focusable = Array.from(box!.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (!first) { return }
      const active = document.activeElement

      if (event.shiftKey) {
        if (active === first || !box!.contains(active)) {
          event.preventDefault()
          last.focus()
        }
      } else if (active === last || !box!.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.body.style.overflow = previousOverflow
    }
  }, [open, isMobile])

  // Anchor the desktop card to the trigger, clamped into the viewport and
  // flipped above if it would overflow the bottom. Mobile centers via CSS.
  React.useLayoutEffect(() => {
    if (!open || isMobile) { return undefined }

    function reposition() {
      const trigger = triggerRef.current
      if (!trigger) { return }

      const rect = trigger.getBoundingClientRect()
      const gap = 12
      let top = rect.bottom + gap
      let left = rect.left

      const box = boxRef.current
      if (box) {
        const maxLeft = window.innerWidth - box.offsetWidth - gap
        left = Math.max(gap, Math.min(left, maxLeft))
        const maxTop = window.innerHeight - box.offsetHeight - gap
        if (top > maxTop) { top = Math.max(gap, rect.top - box.offsetHeight - gap) }
      }

      setCoords({ top, left })
    }

    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open, isMobile, items.length])

  function renderPopover() {
    const columns = items.length > COLUMN_THRESHOLD
    const boxStyle: React.CSSProperties =
      isMobile || !coords ? {} : { position: 'fixed', top: coords.top, left: coords.left }

    const popover = (
      <div className={`trial-explainer trial-explainer--${isMobile ? 'mobile' : 'desktop'}`}>
        {isMobile ? <div className="trial-explainer__backdrop" /> : null}
        <div
          ref={boxRef}
          className="trial-explainer__box"
          role="dialog"
          aria-labelledby={titleId}
          aria-modal={isMobile ? true : undefined}
          tabIndex={-1}
          style={boxStyle}
        >
          {isMobile ? (
            <button
              ref={closeButtonRef}
              type="button"
              className="trial-explainer__close"
              aria-label="Close"
              onClick={close}
            >
              &times;
            </button>
          ) : null}
          <p id={titleId} className="trial-explainer__title">
            Your <strong>{planName}</strong> trial includes:
          </p>
          <hr className="trial-explainer__divider" />
          <ol
            className={`trial-explainer__list${columns ? ' trial-explainer__list--columns' : ''}`}
          >
            {items.map((item) => (
              <li key={item.name} className="trial-explainer__item">
                <span className="trial-explainer__item-name">{item.name}</span>{' '}
                <span className="trial-explainer__item-count">({downloadLabel(item.count)})</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    )

    return createPortal(popover, document.body)
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="trial-explainer__trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
      >
        {children}
      </button>
      {open ? renderPopover() : null}
    </>
  )
}
